import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnInit,
  Renderer2,
  SecurityContext,
  inject,
  viewChild
} from "@angular/core";
import { DomSanitizer, Title } from "@angular/platform-browser";
import {
  Event as RouterEvent,
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  RouterOutlet
} from "@angular/router";
import { LiveChatService } from "@app/core/services/live-chat.service";
import { LoaderService } from "@app/core/services/loader.service";
import { MessageService } from "@app/core/services/message.service";
import { ServiceWorkerService } from "@app/core/services/service-worker.service";
import { SystemSettingsService } from "@app/core/services/system-settings.service";
import { TimeoutService } from "@app/core/services/timeout.service";
import { BaseComponent } from "@app/shared/components/abstracts/base-component";
import { LIVE_CHAT } from "@app/shared/constants/live-chat.constants";
import { ROUTES } from "@app/shared/constants/routes.constants";
import { environment } from "@env/environment";
import { debounceTime, delay } from "rxjs";
import { routeFade } from "./shared/animations/transition.animation";
import { LoaderState } from "./shared/components/loader/loader.model";
import { registerLocaleData } from "@angular/common";
import { LoaderSpinnerOverlayComponent } from "@app/shared/components/loader/loader-spinner-overlay/loader-spinner-overlay.component";
import localeEN from "@angular/common/locales/en";
import localeESUS from "@angular/common/locales/es-US";
import { LANGUAGES } from "@app/shared/constants/localization.constants";
import { LogoComponent } from "@app/shared/components/logo/logo.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ContextMenuService } from "@app/shared/components/context-menu/context-menu.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  animations: [routeFade],
  imports: [LoaderSpinnerOverlayComponent, RouterOutlet, LogoComponent]
})
export class AppComponent extends BaseComponent implements OnInit, AfterViewInit {
  private zone = inject(NgZone);
  private systemSettingsService = inject(SystemSettingsService);
  private timeoutService = inject(TimeoutService);
  private titleService = inject(Title);
  private swService = inject(ServiceWorkerService);
  private loaderService = inject(LoaderService);
  private renderer = inject(Renderer2);
  private messageService = inject(MessageService);
  private domSanitizer = inject(DomSanitizer);
  private cms = inject(ContextMenuService);
  private liveChatService = inject(LiveChatService);

  _spinner = viewChild<ElementRef>("spinner");
  _mainDiv = viewChild<ElementRef>("mainDiv");
  chatRef = viewChild<ElementRef>("chat");

  private _destroyRef = inject(DestroyRef);

  constructor() {
    super();
    registerLocaleData(localeEN, LANGUAGES.ENGLISH.value);
    registerLocaleData(localeESUS, LANGUAGES.SPANISH.value);
  }

  ngOnInit() {
    this._startBreakPointInterceptor();
    this._startLoaderInterceptor();
    this._startMessageInterceptor();
    this._loadSystemSettings();
  }

  ngAfterViewInit() {
    this._loadBackground();
    this._initializeChat();
  }

  /**
   * Used to trigger animation
   * @param outlet
   * @returns
   */
  prepareRoute(outlet: RouterOutlet) {
    return outlet?.isActivated || "";
  }

  get background(): string {
    return this.systemSettingsService.backgroundImage;
  }

  get liveChatType() {
    return this.systemSettingsService.liveChat?.chatType;
  }

  get liveChat() {
    return LIVE_CHAT;
  }

  /**
   * Load / configure system settings
   *
   * NOTE: System setting were fetched during app initialization,
   *       now we need to apply these settings to the application.
   *
   *       If the system settings have loaded properly then continue...
   *       Otherwise... route user to not-available-error module.
   */
  private _loadSystemSettings() {
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);
    const systemSettings = this.systemSettingsService.systemSettings;

    if (!systemSettings) {
      this.routeService.navigate([ROUTES.ERROR, ROUTES.NOT_AVAILABLE]);
      return;
    }

    this.titleService.setTitle(systemSettings.name);
    this.themeService.setThemeAttributes(this.authService?.theme ?? systemSettings.themeSettings, true);
    this.i18nService.language = this.authService.language || systemSettings.localeSettings.language;
    this.timeoutService.timeOutSettings = systemSettings.timeoutSettings;

    if (!navigator.cookieEnabled || !localStorage) {
      this.routeService.navigate([ROUTES.ERROR]);
    } else if (systemSettings.isDisabled && !this.authService.isAuthenticated()) {
      this.routeService.navigate([ROUTES.DISABLED]);
    } else {
      this._navigate();
    }
  }

  /**
   * Manage default navigation on app load.
   */
  private _navigate() {
    // Check for SSO redirect. SSO url needs to be login/sso
    if (window.location.href.includes(`${ROUTES.LOGIN}/${ROUTES.SSO}?`)) {
      return;
    }

    if (this.authService.isAuthenticated()) {
      this.routeService.navigateHome();
    } else {
      this.routeService.navigate([ROUTES.LOGIN]);
    }
  }

  /**
   * Manage mobile vs desktop break point states
   */
  private _startBreakPointInterceptor() {
    this.breakpointService.startBreakPointInterceptor();
  }

  /**
   * Manage displaying of loader / spinner component
   *
   * NOTE: The logic to display the loader happens in NgZone.
   *       This is to improve loader performance and fine tune
   *       when the loader actually displays based on activity
   *       outside of change detection.
   */
  private _startLoaderInterceptor() {
    this.router.events.pipe(delay(10), takeUntilDestroyed(this._destroyRef)).subscribe((event: RouterEvent) => {
      this.cms.closeAll(); // Close all context menus on navigation. Sometimes they do not close if menu items are opened via button
      this._navigationInterceptor(event);
    });

    this.zone.runOutsideAngular(() => {
      this.loaderService.loaderSubject
        .pipe(debounceTime(200), takeUntilDestroyed(this._destroyRef))
        .subscribe((state) => {
          this._loaderInterceptor(state);
        });
    });
  }

  /**
   * Monitor when domain images have been updated
   */
  private _startMessageInterceptor() {
    this.messageService
      .getImageMessage()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._loadBackground();
      });
  }

  /**
   * Manage navigation events
   */
  private _navigationInterceptor(event: RouterEvent) {
    switch (true) {
      case event instanceof NavigationStart: {
        this.loaderService.show();
        break;
      }

      case event instanceof NavigationEnd: {
        this.swService.checkForUpdates();
        this.loaderService.hide();
        break;
      }

      case event instanceof NavigationCancel:
      case event instanceof NavigationError: {
        this.loaderService.clear();
        break;
      }
      case event instanceof GuardsCheckStart: {
        if (this.modalSheetService.isOpen) {
          this.loaderService.clear();
        }
        break;
      }

      default: {
        break;
      }
    }
  }

  /**
   * Show or hide loader / spinner component
   */
  private _loaderInterceptor(state: LoaderState) {
    try {
      const _spinner = this._spinner();
      if (_spinner) {
        if (state.show) {
          this.renderer.setAttribute(_spinner.nativeElement, "class", "spinner-show");
        } else {
          this.renderer.setAttribute(_spinner.nativeElement, "class", "spinner-hide");
        }
      }
    } catch {}
  }

  /**
   * Load background image
   */
  private _loadBackground() {
    try {
      const _mainDiv = this._mainDiv();
      if (_mainDiv) {
        this.renderer.setStyle(
          _mainDiv.nativeElement,
          "background",
          this.domSanitizer.sanitize(
            SecurityContext.STYLE,
            "url(" + this.systemSettingsService.backgroundImage + ") no-repeat center center/cover fixed"
          )
        );
      }
    } catch {}
  }

  /**
   * Initialize live chat for the domain
   */
  private _initializeChat() {
    this.waitUntilObjectIsReady(this.chatRef).subscribe((r) => {
      if (!r) {
        return;
      }
      this.liveChatService.loadLiveChat(this.chatRef());
    });
  }
}
